import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AlertTriangle, MapPin, TrendingUp, Calendar, Filter } from 'lucide-react';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom heat layer component
const HeatLayer = ({ data }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Remove existing heat layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
    }

    // Dynamically import leaflet.heat
    import('leaflet.heat').then(() => {
      // Prepare heat data: [lat, lng, intensity]
      const heatData = data.map(point => [
        point.latitude,
        point.longitude,
        point.intensity || Math.min(point.totalIncidents / 10, 1) // Use API intensity or normalize
      ]);

      // Create heat layer
      heatLayerRef.current = L.heatLayer(heatData, {
        radius: 25,
        blur: 15,
        maxZoom: 17,
        gradient: {
          0.0: 'blue',
          0.2: 'cyan',
          0.4: 'lime',
          0.6: 'yellow',
          0.8: 'orange',
          1.0: 'red'
        }
      }).addTo(map);
    });

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
    };
  }, [data, map]);

  return null;
};

// Custom marker icons based on severity
const createCustomIcon = (severity, count) => {
  const colors = {
    low: '#10B981',
    medium: '#F59E0B',
    high: '#EF4444'
  };

  return L.divIcon({
    html: `
      <div style="
        background-color: ${colors[severity]};
        width: ${Math.min(20 + count * 2, 40)}px;
        height: ${Math.min(20 + count * 2, 40)}px;
        border-radius: 50%;
        border: 2px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">
        ${count}
      </div>
    `,
    className: 'custom-div-icon',
    iconSize: [Math.min(20 + count * 2, 40), Math.min(20 + count * 2, 40)],
    iconAnchor: [Math.min(10 + count, 20), Math.min(10 + count, 20)]
  });
};

const HeatMap = ({ data, loading, onTimeRangeChange, onRegionChange }) => {
  const [viewMode, setViewMode] = useState('heatmap'); // 'heatmap' or 'markers'
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const timeRangeOptions = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
    onTimeRangeChange?.(range);
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    onRegionChange?.(region);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading heat map data...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.heatmapData || data.heatmapData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-center">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No counterfeit detection data available</p>
          <p className="text-sm text-gray-500 mt-2">Try adjusting the time range or region filters</p>
        </div>
      </div>
    );
  }

  // Calculate map center based on data
  const centerLat = data.heatmapData.reduce((sum, point) => sum + point.latitude, 0) / data.heatmapData.length;
  const centerLng = data.heatmapData.reduce((sum, point) => sum + point.longitude, 0) / data.heatmapData.length;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header with controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Counterfeit Detection Heat Map
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Areas with increasing counterfeit detections and suspicious activity
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
            
            <div className="flex bg-gray-100 rounded-md p-1">
              <button
                onClick={() => setViewMode('heatmap')}
                className={`px-3 py-1 text-sm rounded ${
                  viewMode === 'heatmap' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Heat Map
              </button>
              <button
                onClick={() => setViewMode('markers')}
                className={`px-3 py-1 text-sm rounded ${
                  viewMode === 'markers' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Markers
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Time Range
                </label>
                <select
                  value={selectedTimeRange}
                  onChange={(e) => handleTimeRangeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {timeRangeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Region
                </label>
                <select
                  value={selectedRegion}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Regions</option>
                  <option value="South Africa">South Africa</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="Kenya">Kenya</option>
                  <option value="Ghana">Ghana</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Summary stats */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{data.summary?.totalIncidents || 0}</div>
            <div className="text-xs text-gray-600">Total Detections</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{data.summary?.totalCounterfeitReports || 0}</div>
            <div className="text-xs text-gray-600">Verified Detections</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{data.summary?.totalClusters || 0}</div>
            <div className="text-xs text-gray-600">Affected Regions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{Math.round((data.summary?.totalCounterfeitReports / data.summary?.totalIncidents * 100) || 0)}%</div>
            <div className="text-xs text-gray-600">Avg Confidence</div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-96 relative">
        <MapContainer
          center={[centerLat || -26.2041, centerLng || 28.0473]} // Default to Johannesburg
          zoom={6}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {viewMode === 'heatmap' && (
            <HeatLayer data={data.heatmapData} />
          )}
          
          {viewMode === 'markers' && data.heatmapData.map((point, index) => (
            <Marker
              key={index}
              position={[point.latitude, point.longitude]}
              icon={createCustomIcon(point.severity, point.totalIncidents)}
            >
              <Popup>
                <div className="p-2 min-w-64">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${
                      point.severity === 'high' ? 'bg-red-500' :
                      point.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></div>
                    <span className="font-semibold capitalize">{point.severity} Risk Area - {point.city}</span>
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total Incidents:</span>
                      <span className="font-medium">{point.totalIncidents}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Counterfeit Reports:</span>
                      <span className="font-medium text-red-600">{point.counterfeitReports}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Suspicious Validations:</span>
                      <span className="font-medium text-orange-600">{point.suspiciousValidations}</span>
                    </div>
                  </div>
                  
                  {point.regions && point.regions.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Regions:</div>
                      <div className="text-xs text-gray-600">
                        {point.regions.slice(0, 3).join(', ')}
                        {point.regions.length > 3 && ` +${point.regions.length - 3} more`}
                      </div>
                    </div>
                  )}
                  
                  {Object.keys(point.detectionMethods || {}).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Detection Methods:</div>
                      <div className="space-y-1">
                        {Object.entries(point.detectionMethods).slice(0, 3).map(([method, count], idx) => (
                          <div key={idx} className="text-xs text-gray-600 flex justify-between">
                            <span className="capitalize">{method.replace('_', ' ')}</span>
                            <span>{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {point.timeRange && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Time Range:</div>
                      <div className="text-xs text-gray-600">
                        {new Date(point.timeRange.earliest).toLocaleDateString()} - {new Date(point.timeRange.latest).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        
        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg z-10">
          <div className="text-sm font-medium text-gray-700 mb-2">Risk Levels</div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>High Risk (10+ incidents)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span>Medium Risk (5-9 incidents)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Low Risk (1-4 incidents)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;